import { Attribute, Child, DomDirective, HostElement } from './../../libs/core';

/**
 * Generates html-elements of a audio-player in the DOM with a play/pause functionality
 *
 * @example
 * ```html
 * <div class="audioplayer" data-audio-url="aa-test.mp3" aa-audio>
 * ```
 *
 */
@DomDirective({
  selector: 'aa-audio',
})
export class AudioDirective {
  /**
   * Reference to the HTMLElement this directive is applied on.
   *
   * @property {HTMLElement}
   */
  @HostElement()
  private readonly host: HTMLElement;

  @Child('.audioplayer__btn')
  private readonly playPauseButtonElement: HTMLElement;
  @Child('audio')
  private readonly audioElement: HTMLAudioElement;
  @Child('.audioplayer__current-time')
  private readonly currentTime: HTMLElement;
  @Child('.audioplayer__total-time')
  private readonly totalTime: HTMLElement;

  @Attribute('data-audio-url')
  private audioUrl: string | null;

  private isPlaying: boolean;
  private theCurrentTime: number;


  connected(): void {
    this.generateHTML();
    this.setup();
    this.setAudioEventListeners();
  }

  private setup(): void {
    this.isPlaying = false;
    this.theCurrentTime = 0;
  }

  private setAudioEventListeners(): void {

    this.playPauseButtonElement.addEventListener('click', () =>
        this.togglePlayPause(),
    );
    this.audioElement.addEventListener('timeupdate', () =>
        this.updateProgress(),
    );
    this.audioElement.addEventListener(
        'loadedmetadata',
        () =>
            (this.totalTime.textContent = AudioDirective.formatTime(
                this.audioElement.duration,
            )),
    );
    this.audioElement.addEventListener('ended', () => this.resetPlayer());
}

  getBarPercentage(e: any, className: string): number {
    const bar = e.target.className === className ? e.target : e.target.parentElement;
    const rect = bar.getBoundingClientRect();
    const clientPos = e.clientX - rect.x;
    const barPercentage = clientPos / rect.width;
    this.audioElement.currentTime = this.audioElement.duration * barPercentage;
    this.updateDotLocation( Math.floor(barPercentage * 100));
    return barPercentage;
  }

  private updateProgress(): void {
    const current = this.audioElement.currentTime;
    this.currentTime.textContent = AudioDirective.formatTime(current);
    const duration = this.audioElement.duration;
    const currentTime = this.audioElement.currentTime;
    const percentage =  Math.floor((currentTime / duration) * 100);
    this.updateDotLocation(percentage);
  }

  private updateDotLocation(location: number): void{
    const bar = document.getElementById('progress-bar-dot');
    if(bar){
        bar.style.left = location+'%';
    }
  }

  private static formatTime(time: number): string {
    const min = Math.floor(time / 60);
    const sec = Math.floor(time % 60);
    return min + ':' + (sec < 10 ? '0' + sec : sec);
  }

  resetPlayer(): void {
    this.audioElement.load();
    this.playPauseButtonElement.classList.remove('audioplayer__btn--pause');
  }

  togglePlayPause(): void {
    if (this.audioElement.paused) {
      this.playPauseButtonElement.classList.add('audioplayer__btn--pause');
      this.audioElement.play();
    } else {
      this.playPauseButtonElement.classList.remove('audioplayer__btn--pause');
      this.audioElement.pause();
    }
  }

  // Creates the HTML with the supporting attributes of the audio-player
  private generateHTML(): void {
    const buttonAudio = document.createElement('div');
    buttonAudio.classList.add('audioplayer__btn');

    const controlsAudio = document.createElement('div');
    controlsAudio.classList.add('audioplayer__controls');

    const progressBar = document.createElement('div');
    progressBar.classList.add('audioplayer__progress-bar');
    progressBar.addEventListener('click', (e) => {this.getBarPercentage(e, 'audioplayer__progress-bar')});

    const progressBarDot = document.createElement('div');
    progressBarDot.classList.add('audioplayer__progress-bar-dot');
    progressBarDot.addEventListener('click', (e) => {this.getBarPercentage(e, 'audioplayer__progress-bar')});
    progressBarDot.id = 'progress-bar-dot'
    progressBar.appendChild(progressBarDot);

    const smallCurrentTime = document.createElement('small');
    smallCurrentTime.classList.add('audioplayer__current-time');
    smallCurrentTime.innerHTML = '0:00';
    const smallTotalTime = document.createElement('small');
    smallTotalTime.classList.add('audioplayer__total-time');
    smallTotalTime.innerHTML = '0:00';
    controlsAudio.appendChild(smallCurrentTime);
    controlsAudio.appendChild(progressBar);
    controlsAudio.appendChild(smallTotalTime);

    // audio element
    const audioElement = document.createElement('audio');
    audioElement.setAttribute('crossorigin', '');
    audioElement.src = '' + this.audioUrl;
    audioElement.setAttribute('type', 'mp3');
    audioElement.setAttribute('preload', 'auto');

    this.host.appendChild(buttonAudio);
    this.host.appendChild(controlsAudio);
    this.host.appendChild(audioElement);
  }
}
